import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { diagnosticsAPI } from '../services/api';

function Diagnostics() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [systemStatus, setSystemStatus] = useState(null);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logLines, setLogLines] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const wsRef = useRef(null);
  const logContainerRef = useRef(null);

  useEffect(() => {
    loadSystemStatus();

    // Listen for sudo session established event
    const handleSudoSession = () => {
      loadSystemStatus();
    };

    window.addEventListener('sudo-session-established', handleSudoSession);

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      window.removeEventListener('sudo-session-established', handleSudoSession);
    };
  }, []);

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logLines]);

  const loadSystemStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await diagnosticsAPI.getStatus();
      setSystemStatus(response.data.status);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load system status');
    } finally {
      setLoading(false);
    }
  };

  const runDiagnostics = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await diagnosticsAPI.runDiagnostics('full_diagnostic');
      setDiagnosticResults(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Diagnostics failed');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await diagnosticsAPI.getLogs({ lines: 100 });
      setLogs(response.data.lines || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const startLogStreaming = () => {
    const token = localStorage.getItem('token');
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/ws/logs/`;

    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      setIsStreaming(true);
      setLogLines([]);
      wsRef.current.send(
        JSON.stringify({
          action: 'start',
          log_file: '/var/log/samba/log.samba',
          token: token,
        })
      );
    };

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'log') {
        setLogLines((prev) => [...prev, data.message]);
      } else if (data.type === 'error') {
        setError(data.message);
      }
    };

    wsRef.current.onerror = (error) => {
      setError('WebSocket error occurred');
      setIsStreaming(false);
    };

    wsRef.current.onclose = () => {
      setIsStreaming(false);
    };
  };

  const stopLogStreaming = () => {
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({ action: 'stop' }));
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsStreaming(false);
  };

  const clearLogStream = () => {
    setLogLines([]);
  };

  useEffect(() => {
    if (tabValue === 0) {
      loadSystemStatus();
    } else if (tabValue === 2) {
      loadLogs();
    }
  }, [tabValue]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Diagnostics
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="System Status" />
          <Tab label="Run Diagnostics" />
          <Tab label="Log Viewer" />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3,
                 flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
              <Typography variant="h6">Current System Status</Typography>
              <IconButton onClick={loadSystemStatus} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : systemStatus ? (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Samba AD DC Service
                            </Typography>
                            <Chip
                              label={systemStatus.samba_ad_dc ? 'Running' : 'Stopped'}
                              color={systemStatus.samba_ad_dc ? 'success' : 'error'}
                              sx={{ mt: 1 }}
                            />
                          </Box>
                          {/* Start / Stop / Restart now live on the AGENT
                              card, not here. A page-level button acts on
                              whichever agent happens to be selected, which is
                              ambiguous the moment there is more than one
                              domain controller -- and the ambiguity is worst
                              for exactly the action that causes an outage. */}
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<ArrowIcon />}
                            onClick={() => navigate('/agents')}
                          >
                            Service controls
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Domain
                        </Typography>
                        <Typography variant="body1">
                          {systemStatus.domain || 'N/A'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Realm
                        </Typography>
                        <Typography variant="body1">
                          {systemStatus.realm || 'N/A'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          LDAP URI
                        </Typography>
                        <Typography variant="body1">
                          {systemStatus.ldap_uri || 'N/A'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            ) : null}
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <Button
              variant="contained"
              onClick={runDiagnostics}
              disabled={loading}
              sx={{ mb: 3 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Run Full Diagnostics'}
            </Button>

            {diagnosticResults && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Diagnostic Results
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Completed in {diagnosticResults.duration_ms}ms
                </Alert>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(diagnosticResults.results, null, 2)}
                  </pre>
                </Paper>
              </Box>
            )}
          </Box>
        )}

        {tabValue === 2 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3,
                 flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
              <Typography variant="h6">Recent Logs</Typography>
              <IconButton onClick={loadLogs}>
                <RefreshIcon />
              </IconButton>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  maxHeight: 500,
                  overflow: 'auto',
                }}
              >
                {logs.map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </Paper>
            )}
          </Box>
        )}

      </Paper>
    </Box>
  );
}

export default Diagnostics;
